import React, { useState } from "react";
import axios from "axios";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const NewsletterForm = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [hp, setHp] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    if (!EMAIL_RE.test(email.trim())) {
      setError(t.footer.emailError);
      return;
    }
    if (!consent) {
      setError(t.form.errors.consent);
      return;
    }
    setError("");
    setStatus("sending");
    setMessage(t.form.sending);
    try {
      const res = await axios.post(`${API}/newsletter`, {
        email: email.trim(),
        consent: true,
        hp,
      });
      setEmail("");
      setConsent(false);
      setStatus("sent");
      setMessage(res.data?.status === "already_subscribed" ? t.newsletter.already : t.newsletter.sent);
    } catch (err) {
      setStatus("error");
      setMessage(err?.response?.status === 429 ? t.form.rate : t.newsletter.error);
    }
  };

  return (
    <form onSubmit={subscribe} noValidate className="mt-6 max-w-md">
      <label
        htmlFor="footer-email"
        className="block text-xs font-semibold uppercase tracking-widest text-[#E6D9B6]/70 mb-2"
      >
        {t.footer.emailLabel}
      </label>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          id="footer-email"
          data-testid="newsletter-email"
          type="email"
          required
          aria-invalid={!!error}
          aria-describedby={error ? "footer-email-error" : undefined}
          placeholder={t.footer.placeholder}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError("");
          }}
          className={`flex-1 min-h-[48px] bg-[#1c1c1c] rounded-xl border px-4 py-3 text-[#F5F1E8] placeholder:text-[#F5F1E8]/40 focus:outline-none focus:ring-2 focus:ring-[#F4D35E]/50 transition-colors ${
            error ? "border-red-400" : "border-[#F5F1E8]/25 focus:border-[#F4D35E]"
          }`}
        />
        <button
          data-testid="newsletter-submit"
          type="submit"
          disabled={status === "sending"}
          className="min-h-[48px] bg-[#F4D35E] text-[#3B2523] font-semibold px-6 py-3 rounded-xl whitespace-nowrap transition-all hover:-translate-y-0.5 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#121212]"
        >
          {status === "sending" ? t.form.sending : t.footer.subscribe}
        </button>
      </div>

      <label className="flex items-start gap-3 mt-4 cursor-pointer text-[#E6D9B6]/70">
        <input
          type="checkbox"
          data-testid="newsletter-consent"
          checked={consent}
          onChange={(e) => {
            setConsent(e.target.checked);
            if (error) setError("");
          }}
          className="mt-0.5 w-5 h-5 shrink-0 accent-[#F4D35E]"
        />
        <span className="text-sm leading-relaxed">{t.newsletter.consent}</span>
      </label>

      {/* honeypot */}
      <input
        type="text"
        name="company_url"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        className="absolute w-px h-px -left-[9999px] opacity-0"
      />

      <div aria-live="polite" role="status" data-testid="newsletter-status">
        {error && (
          <p id="footer-email-error" className="text-red-400 text-sm mt-2">
            {error}
          </p>
        )}
        {!error && message && (
          <p className={`text-sm mt-2 ${status === "error" ? "text-red-400" : "text-[#F4D35E]"}`}>
            {message}
          </p>
        )}
      </div>
    </form>
  );
};

export default NewsletterForm;
